import React, { useEffect, useMemo, useState, startTransition } from 'react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { ResponsiveBackgroundContainerBanner } from '@/organisms/WatchProjectHero/ResponsiveBackgroundContainer'
import { Page } from '@/page'
import { useScrollToHash } from '@/utils/useScrollToHash'
import { WatchBackground } from './WatchBackground'
import { useWatchPageContext } from './WatchPageContext'

// Dynamic imports for below-the-fold components
const AppPromoSection = dynamic(() =>
  import('../LandingView/AppPromoSection').then((mod) => ({ default: mod.AppPromoSection })),
)
const VideoModal = dynamic(() => import('@/molecules/VideoModal').then((mod) => ({ default: mod.VideoModal })), {
  ssr: false,
})

interface WatchLandingViewProps {
  discoverTabs?: boolean
}

export const WatchLandingView: React.FC<WatchLandingViewProps> = ({ discoverTabs = false }) => {
  const [showModal, setShowModal] = useState(false)
  const searchParams = useSearchParams()
  const welcomeUrl = 'https://content.angelstudios.com/watch/Angel_Welcome_Neal_Hook1_Outro1_16x9_V5_2024-5-11_CJ.mp4'
  const { page, pageDataContext, currentBanner } = useWatchPageContext()

  useEffect(() => {
    if (searchParams.has('scene') && searchParams.get('scene') === 'neal-welcome-2024') {
      startTransition(() => {
        setShowModal(true)
      })
    }
  }, [searchParams])

  const handleClose = () => {
    setShowModal(false)
  }

  useScrollToHash(1000)

  const responsiveBackgroundProps = useMemo(() => {
    const banner = currentBanner
    if (!banner) return {}

    if (banner.type === 'generic') {
      return {
        cloudinaryImgPath: banner?.genericConfig?.landscapeCloudinaryPath,
        cloudinaryTabletImgPath: banner?.genericConfig?.landscapeCloudinaryPath,
        cloudinaryMobileImgPath: banner?.genericConfig?.cloudinaryPath,
      }
    } else {
      return {
        cloudinaryImgPath:
          banner?.project?.title?.landscapeBackdrop?.cloudinaryPath ??
          banner?.project?.title?.landscapeNonTitle?.cloudinaryPath ??
          banner?.project?.discoveryPosterLandscapeCloudinaryPath,
        cloudinaryTabletImgPath:
          banner?.project?.title?.landscapeBackdrop?.cloudinaryPath ??
          banner?.project?.title?.landscapeNonTitle?.cloudinaryPath ??
          banner?.project?.discoveryPosterLandscapeCloudinaryPath,
        cloudinaryMobileImgPath:
          banner?.project?.title?.squareNonTitle?.cloudinaryPath ??
          banner?.project?.title?.portraitBackdrop?.cloudinaryPath ??
          banner?.project?.title?.portraitNonTitle?.cloudinaryPath ??
          banner?.project?.discoveryPosterCloudinaryPath,
      }
    }
  }, [currentBanner])

  return (
    <div className="relative overflow-hidden">
      <>
        {/* Desktop background */}
        <div className="hidden lg:flex">
          <ResponsiveBackgroundContainerBanner
            cloudinaryImgPath={responsiveBackgroundProps.cloudinaryImgPath ?? ''}
            cloudinaryTabletImgPath={responsiveBackgroundProps.cloudinaryTabletImgPath ?? ''}
            cloudinaryMobileImgPath={responsiveBackgroundProps.cloudinaryMobileImgPath ?? ''}
            theaterBackgroundPosition="bg-right-bottom"
          />
        </div>
        {/* Mobile / tablet background */}
        <div className="flex lg:hidden">
          <WatchBackground />
        </div>
      </>

      <div className="pt-24 text-white lg:pt-0">
        {!!page && !!pageDataContext && (
          <section className={classNames('flex flex-col gap-y-12', 'md:pt-4 lg:pt-0')}>
            <Page
              page={page}
              pageDataContext={{ ...pageDataContext, contentItemConfig: { padding: true, discoverTabs } }}
            />
          </section>
        )}

        <section className="relative w-full bg-core-gray-950">
          <LazyHydrate whenVisible>
            <AppPromoSection />
          </LazyHydrate>
        </section>
      </div>
      <VideoModal showModal={showModal} videoUrl={welcomeUrl} handleClose={handleClose} />
    </div>
  )
}
